import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, RefreshCw, ListChecks } from "lucide-react";
import Navbar from "../components/Navbar";
import { TABS, getItemsForDestination } from "../data/checklistData";
import { toast } from "sonner";
import { ChecklistProgress } from "../components/checklist/ChecklistProgress";
import { ChecklistTabs } from "../components/checklist/ChecklistTabs";
import { ChecklistItems } from "../components/checklist/ChecklistItems";
import { ChecklistAddItem } from "../components/checklist/ChecklistAddItem";
import { devLog } from "../lib/devLog";

const LORA  = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LS_CHECKLIST = "voyage_r_checklist";
const LS_TRIP      = "voyage_r_active_trip";

const EMPTY = {
  checked: { admin: {}, valise: {}, tech: {} },
  custom:  { admin: [], valise: [], tech: [] },
};

function initState() {
  try {
    const raw = localStorage.getItem(LS_CHECKLIST);
    if (!raw) return EMPTY;
    const p = JSON.parse(raw);
    return {
      checked: { ...EMPTY.checked, ...p.checked },
      custom:  { ...EMPTY.custom,  ...p.custom  },
    };
  } catch { return EMPTY; }
}

function getDestination() {
  try {
    const trip = JSON.parse(localStorage.getItem(LS_TRIP) || "null");
    return trip?.destination || null;
  } catch { return null; }
}

// No-destination placeholder
function NoTrip() {
  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-20 pb-16 text-center">
        <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border-2 border-[#DCD4C6] rounded-full">
          <ListChecks size={28} color="#C17F59" strokeWidth={1.5} />
        </div>
        <h1 className="text-4xl mb-3" style={{ ...SERIF, fontWeight: 600, color: "#2B2623", fontStyle: "italic" }}>
          Ma Valise
        </h1>
        <p className="text-sm text-[#9A8F86] italic mb-2" style={LORA}>
          Aucun voyage actif détecté.
        </p>
        <p className="text-sm text-[#5C554E] mb-8 leading-relaxed" style={LORA}>
          Planifiez votre voyage pour obtenir une liste de bagages personnalisée —
          adaptée à votre destination, avec les bons documents, équipements et conseils.
        </p>
        <Link
          to="/plan"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#C17F59] text-[#F9F6F0] text-sm hover:bg-[#A86A46] transition-colors"
          style={{ ...LORA, borderRadius: "2px", fontWeight: 600 }}
          data-testid="checklist-cta-plan"
        >
          <MapPin size={15} strokeWidth={1.5} />
          Planifier mon voyage
        </Link>
      </div>
    </div>
  );
}

export default function ChecklistPage() {
  const navigate    = useNavigate();
  const destination = getDestination();
  const [activeTab, setActiveTab] = useState("admin");
  const [state, setState]         = useState(initState);
  const [inputs, setInputs]       = useState({ admin: "", valise: "", tech: "" });

  const items = getItemsForDestination(destination || "");

  // Persist state
  useEffect(() => {
    try { localStorage.setItem(LS_CHECKLIST, JSON.stringify(state)); } catch (e) { devLog("warn", "[ChecklistPage] localStorage write failed:", e); }
  }, [state]);

  // Global progress
  const { total, done, pct } = (() => {
    let t = 0, d = 0;
    TABS.forEach(({ id }) => {
      const all = [...(items[id] || []), ...(state.custom[id] || [])];
      t += all.length;
      all.forEach(item => { if (state.checked[id]?.[item.id]) d++; });
    });
    return { total: t, done: d, pct: t > 0 ? Math.round((d / t) * 100) : 0 };
  })();

  const toggle = (tabId, itemId) => {
    setState(s => ({
      ...s,
      checked: {
        ...s.checked,
        [tabId]: { ...s.checked[tabId], [itemId]: !s.checked[tabId]?.[itemId] },
      },
    }));
  };

  const addItem = (tabId) => {
    const text = inputs[tabId]?.trim();
    if (!text) return;
    const id = `c_${tabId}_${Date.now()}`;
    setState(s => ({
      ...s,
      custom: { ...s.custom, [tabId]: [...(s.custom[tabId] || []), { id, text }] },
    }));
    setInputs(i => ({ ...i, [tabId]: "" }));
    toast.success("Élément ajouté");
  };

  const removeItem = (tabId, itemId) => {
    setState(s => {
      const { [itemId]: _removed, ...rest } = s.checked[tabId] || {};
      return {
        ...s,
        custom:  { ...s.custom,  [tabId]: (s.custom[tabId] || []).filter(i => i.id !== itemId) },
        checked: { ...s.checked, [tabId]: rest },
      };
    });
  };

  const reset = () => {
    if (!window.confirm("Réinitialiser toute la liste (coches et éléments personnalisés) ?")) return;
    setState(EMPTY);
    toast.success("Liste réinitialisée");
  };

  if (!destination) return <NoTrip />;

  const tabItems    = items[activeTab] || [];
  const customItems = state.custom[activeTab] || [];
  const checkedMap  = state.checked[activeTab] || {};
  const tabDone     = [...tabItems, ...customItems].filter(i => checkedMap[i.id]).length;
  const tabTotal    = tabItems.length + customItems.length;

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />

      <div className="max-w-md mx-auto px-4 pt-6 pb-24">

        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="text-[#9A8F86] hover:text-[#2B2623] transition-colors mt-1"
              data-testid="btn-back"
            >
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
            <div>
              <h1 className="text-4xl" style={{ ...SERIF, fontWeight: 600, color: "#2B2623", fontStyle: "italic" }}>
                Ma Valise
              </h1>
              <p className="text-xs text-[#9A8F86] mt-0.5 flex items-center gap-1" style={LORA}>
                <MapPin size={10} strokeWidth={2} />
                {destination} · Fonctionne hors-ligne
              </p>
            </div>
          </div>
          <button
            onClick={reset}
            className="p-1.5 text-[#9A8F86] hover:text-[#C17F59] transition-colors mt-1"
            title="Réinitialiser la liste"
            data-testid="btn-reset-checklist"
          >
            <RefreshCw size={14} strokeWidth={1.5} />
          </button>
        </div>

        <ChecklistProgress done={done} total={total} pct={pct} />

        <ChecklistTabs
          tabs={TABS}
          items={items}
          state={state}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <ChecklistItems
          tabItems={tabItems}
          customItems={customItems}
          checkedMap={checkedMap}
          activeTab={activeTab}
          onToggle={toggle}
          onRemove={removeItem}
        />

        <ChecklistAddItem
          activeTab={activeTab}
          value={inputs[activeTab]}
          onChange={(val) => setInputs(i => ({ ...i, [activeTab]: val }))}
          onAdd={() => addItem(activeTab)}
        />

        {/* Tab progress note */}
        <p
          className="text-xs text-[#9A8F86] text-center mt-4"
          style={LORA}
          data-testid="tab-progress-summary"
        >
          {tabDone} / {tabTotal} cochés dans cet onglet
        </p>

      </div>
    </div>
  );
}
